'use client';
import { useState } from 'react';
import { useRouter } from 'next/navigation';
import { HugeiconsIcon, type IconSvgElement } from '@hugeicons/react';
import {
  ContentWritingIcon,
  GlobeIcon,
  Analytics01Icon,
  ChatBotIcon,
  Activity01Icon,
  Link01Icon,
  Search01Icon,
} from '@hugeicons/core-free-icons';
import { useSession } from '@/lib/auth-client';
import { useQuery } from 'convex/react';
import { api } from '@/convex/_generated/api';
import { trackPlanSelection } from '@/lib/analytics';
import { Container, SectionLabel } from '../ReferenceChrome';

/* ─── Feature data ───────────────────────────────────────────────────────────── */

const STARTER_FEATURES: Array<{ icon: IconSvgElement; text: string }> = [
  { icon: GlobeIcon, text: '1 site monitored' },
  { icon: ChatBotIcon, text: '100 AI agent credits / month' },
  { icon: Analytics01Icon, text: 'Competitor keyword gap discovery' },
  { icon: Activity01Icon, text: 'Realtime market data' },
  { icon: Link01Icon, text: 'Backlink analysis' },
  { icon: Search01Icon, text: 'Keyword research' },
];

const PRO_FEATURES: Array<{ icon: IconSvgElement; text: string }> = [
  { icon: GlobeIcon, text: '3 sites monitored' },
  { icon: ChatBotIcon, text: '300 AI agent credits / month' },
  { icon: Analytics01Icon, text: 'Competitor keyword gap discovery' },
  { icon: Activity01Icon, text: 'Realtime market data' },
  { icon: Link01Icon, text: 'Backlink analysis' },
  { icon: Search01Icon, text: 'Keyword research' },
];

/* ─── Types ──────────────────────────────────────────────────────────────────── */

type Billing = 'monthly' | 'yearly';

/* ─── Billing toggle ─────────────────────────────────────────────────────────── */

function BillingToggle({
  value,
  onChange,
}: {
  value: Billing;
  onChange: (v: Billing) => void;
}) {
  return (
    <div className="inline-flex items-center rounded-full border border-gray-200 bg-gray-50/80 p-0.5 text-[13px] font-medium">
      <button
        type="button"
        onClick={() => onChange('monthly')}
        className={`rounded-full px-3.5 py-1 transition-all duration-150 ${
          value === 'monthly'
            ? 'bg-white text-gray-900 shadow-[inset_0_1px_0px_rgba(255,255,255,0.8),0_0_0_1px_rgba(0,0,0,0.08),0_2px_6px_0_rgba(0,0,0,0.06)] ring-1 ring-inset ring-gray-300/70'
            : 'text-gray-500 hover:text-gray-700'
        }`}
      >
        Monthly
      </button>
      <button
        type="button"
        onClick={() => onChange('yearly')}
        className={`flex items-center gap-1.5 rounded-full px-3.5 py-1 transition-all duration-150 ${
          value === 'yearly'
            ? 'bg-white text-gray-900 shadow-[inset_0_1px_0px_rgba(255,255,255,0.8),0_0_0_1px_rgba(0,0,0,0.08),0_2px_6px_0_rgba(0,0,0,0.06)] ring-1 ring-inset ring-gray-300/70'
            : 'text-gray-500 hover:text-gray-700'
        }`}
      >
        Yearly
        <span className={`rounded-sm px-1.5 py-0.5 text-[10px] font-semibold leading-none ${
          value === 'yearly'
            ? 'bg-[#129990]/10 text-[#129990]'
            : 'bg-gray-200/80 text-gray-500'
        }`}>
          2 months free
        </span>
      </button>
    </div>
  );
}

/* ─── Gloss CTA button ───────────────────────────────────────────────────────── */

function GlossButton({
  href,
  disabled,
  label,
  onClick,
}: {
  href: string;
  disabled: boolean;
  label: string;
  onClick?: () => void;
}) {
  const router = useRouter();

  if (disabled) {
    return (
      <div className="flex w-full cursor-default select-none items-center justify-center rounded-md border border-gray-200 bg-gray-50 py-2 text-sm font-medium text-gray-400">
        {label}
      </div>
    );
  }
  return (
    <button
      type="button"
      onClick={() => {
        onClick?.();
        router.push(href);
      }}
      className="relative flex w-full cursor-pointer items-center justify-center rounded-md border border-[#129990]/50 bg-gradient-to-r from-[#129990] to-[#1EAD9F] py-2 text-sm font-medium text-white shadow-[inset_0_1px_0px_rgba(255,255,255,0.2),0_0_0_1px_rgba(18,153,144,0.3),0_2px_6px_0_rgba(18,153,144,0.25)] transition-all duration-75 hover:brightness-105 active:translate-y-[2px] active:shadow-[inset_0_1px_0px_rgba(255,255,255,0.2),0_0_0_1px_rgba(18,153,144,0.3)]"
    >
      {label}
    </button>
  );
}

/* ─── Feature row ────────────────────────────────────────────────────────────── */

function FeatureRow({ icon, text }: { icon: IconSvgElement; text: string }) {
  return (
    <li className="flex items-center gap-3">
      <span className="flex size-7 shrink-0 items-center justify-center rounded-sm bg-[#129990]/[0.07] text-[#129990]">
        <HugeiconsIcon icon={icon} size={17} strokeWidth={1.5} />
      </span>
      <span className="text-sm font-light text-gray-600">{text}</span>
    </li>
  );
}

/* ─── Section ────────────────────────────────────────────────────────────────── */

export function PricingRef({ hideHeader }: { hideHeader?: boolean } = {}) {
  const [billing, setBilling] = useState<Billing>('monthly');
  const { data: session } = useSession();
  const user = useQuery(api.users.getCurrentUser);
  const hasSubscription = user?.subscriptionStatus === 'active';
  const userPlan = user?.plan ?? undefined;

  const hasSession = !!session;

  function getCTA(planId: 'base' | 'pro') {
    if (hasSession && hasSubscription) {
      const isCurrent =
        (planId === 'base' && userPlan === 'STARTER') ||
        (planId === 'pro' && userPlan === 'PRO');
      if (isCurrent) return { label: 'Current Plan', href: '/dashboard/settings', disabled: true };
      return { label: 'Go to Dashboard', href: '/dashboard', disabled: false };
    }
    if (!hasSession) {
      return {
        label: 'Get Started',
        href: '/sign-up',
        disabled: false,
      };
    }
    return {
      label: 'Get Started',
      href: '/onboarding?step=7',
      disabled: false,
    };
  }

  const basePriceStr = billing === 'yearly' ? '12.50' : '15';
  const proPriceStr = billing === 'yearly' ? '20.83' : '25';
  const priceSuffix = billing === 'yearly' ? '/mo, billed yearly' : '/month';

  const baseCTA = getCTA('base');
  const proCTA = getCTA('pro');

  return (
    <section id="pricing" className="pb-16 sm:pb-24">
      <div
        className="rail-inset-x border-t border-gray-100"
      />
      <Container className={`pt-16 sm:pt-24 ${hideHeader ? 'hidden' : ''}`}>
        {/* ── Section header ── */}
        <div className="text-center">
          <SectionLabel>Pricing</SectionLabel>
          <h2 className="mt-4 text-3xl font-normal leading-tight tracking-tight text-gray-900 sm:text-[2.75rem]">
            Simple, Transparent Pricing
          </h2>
          <p className="mx-auto mt-3 max-w-sm text-sm font-light leading-relaxed text-gray-500">
            No long-term contracts. Cancel anytime. Choose the plan that fits your content goals.
          </p>
        </div>
      </Container>

      {/* ── Unified card container (edges align with vertical rails) ── */}
      <div
        className="rail-inset-x mt-10 border-t border-b border-gray-200"
      >
        <div className="grid grid-cols-1 sm:grid-cols-2 divide-y divide-gray-200 sm:divide-x sm:divide-y-0">

            {/* ════ STARTER PLAN ════ */}
            <div className="flex flex-col relative overflow-hidden">
              {/* Header row */}
              <div className="border-b border-gray-100 px-6 py-5">
                <h3 className="text-xl font-semibold text-gray-900">Starter</h3>
                <p className="mt-1 text-xs font-light text-gray-500">
                  Best for: One site, getting traction
                </p>
              </div>

              {/* Body */}
              <div className="flex flex-1 flex-col px-6 py-6">
                {/* Price */}
                <div className="flex items-baseline gap-1">
                  <span className="text-[2.5rem] font-bold leading-none tracking-tight text-gray-900">
                    ${basePriceStr}
                  </span>
                  <span className="text-xs font-light text-gray-400">{priceSuffix}</span>
                </div>

                {/* Description */}
                <p className="mt-3 text-sm font-light leading-relaxed text-gray-500">
                  Everything you need to close keyword gaps and build consistent organic traffic.
                </p>

                {/* Features */}
                <div className="mt-6 border-t border-gray-100 pt-5">
                  <ul className="space-y-2.5">
                    {STARTER_FEATURES.map((f) => (
                      <FeatureRow key={f.text} icon={f.icon} text={f.text} />
                    ))}
                  </ul>
                </div>

                {/* CTA — pinned to bottom via mt-auto */}
                <div className="mt-auto pt-7">
                  <GlossButton
                    href={baseCTA.href}
                    disabled={baseCTA.disabled}
                    label={baseCTA.label}
                    onClick={() => !baseCTA.disabled && trackPlanSelection('base', billing)}
                  />
                </div>
              </div>
            </div>

            {/* ════ PRO PLAN ════ */}
            <div className="flex flex-col bg-[#129990]/[0.02] relative">
              {/* Header row — toggle lives here */}
              <div className="flex items-start justify-between border-b border-gray-100 px-6 py-5">
                <div>
                  <h3 className="text-xl font-semibold text-gray-900">Pro</h3>
                  <p className="mt-1 text-xs font-light text-gray-500">
                    Best for: Multiple sites, scaling fast
                  </p>
                </div>
                <div className="ml-3 shrink-0">
                  <BillingToggle value={billing} onChange={setBilling} />
                </div>
              </div>

              {/* Body */}
              <div className="flex flex-1 flex-col px-6 py-6">
                {/* Price */}
                <div className="flex items-baseline gap-1">
                  <span className="text-[2.5rem] font-bold leading-none tracking-tight text-gray-900">
                    ${proPriceStr}
                  </span>
                  <span className="text-xs font-light text-gray-400">{priceSuffix}</span>
                </div>

                {/* Description */}
                <p className="mt-3 text-sm font-light leading-relaxed text-gray-500">
                  The full growth engine: more content, more sites, and priority support.
                </p>

                {/* Features */}
                <div className="mt-6 border-t border-gray-100 pt-5">
                  <ul className="space-y-2.5">
                    {PRO_FEATURES.map((f) => (
                      <FeatureRow key={f.text} icon={f.icon} text={f.text} />
                    ))}
                  </ul>
                </div>

                {/* CTA — pinned to bottom via mt-auto */}
                <div className="mt-auto pt-7">
                  <GlossButton
                    href={proCTA.href}
                    disabled={proCTA.disabled}
                    label={proCTA.label}
                    onClick={() => !proCTA.disabled && trackPlanSelection('pro', billing)}
                  />
                </div>
              </div>
            </div>

          </div>
        </div>
    </section>
  );
}
